import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ObjektStrukturService, Raum } from '../services/objekt-struktur.service';

export interface PlanDrawIndicatorDialogData {
  standortId: string;
  sektionId: string;
  /** Existing room indicators that are not yet placed on the canvas. */
  existing: any[];
}

export type PlanDrawIndicatorDialogResult =
  | { mode: 'existing'; indicator: any }
  | { mode: 'create'; indikatorId: string; roomId: string };

@Component({
  selector: 'app-plan-draw-indicator-dialog',
  template: `
    <h2 mat-dialog-title>Indikator hinzufügen</h2>
    <mat-dialog-content class="dialog-content">
      <mat-tab-group [selectedIndex]="data.existing && data.existing.length ? 0 : 1">
        <!-- Existing -->
        <mat-tab label="Vorhanden">
          <div class="tab-body">
            <p *ngIf="data.existing.length === 0" class="empty">
              Keine weiteren Indikatoren verfügbar.
            </p>
            <mat-radio-group *ngIf="data.existing.length > 0" [(ngModel)]="selectedExistingId" class="radio-list">
              <mat-radio-button *ngFor="let ri of data.existing" [value]="ri.id" class="radio-item">
                <span class="num" *ngIf="ri.indikator_number">{{ ri.indikator_number }}</span>
                {{ ri.name || ri.indikator?.beschreibung || ri.id }}
              </mat-radio-button>
            </mat-radio-group>
          </div>
        </mat-tab>

        <!-- Create new -->
        <mat-tab label="Neu erstellen">
          <div class="tab-body">
            <div class="mb-4">
              <label class="block text-xs font-medium text-gray-500 mb-1" for="tw-indikator">Indikator-Typ</label>
              <div class="relative">
                <select id="tw-indikator"
                        class="block w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-9 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        [(ngModel)]="selectedIndikatorId">
                  <option [ngValue]="null" disabled>Bitte wählen…</option>
                  <option *ngFor="let it of catalog" [ngValue]="it.id">
                    {{ it.beschreibung || it.code || it.name }}
                  </option>
                </select>
                <svg class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>

            <div class="mb-2">
              <label class="block text-xs font-medium text-gray-500 mb-1" for="tw-raum">Raum</label>
              <div class="relative">
                <select id="tw-raum"
                        class="block w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-9 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        [(ngModel)]="selectedRoomId">
                  <option [ngValue]="null" disabled>Bitte wählen…</option>
                  <option *ngFor="let r of rooms" [ngValue]="r.id">{{ r.name }}</option>
                </select>
                <svg class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>
            <p *ngIf="!loading && rooms.length === 0" class="empty">
              Diese Sektion hat keine Räume. Bitte zuerst einen Raum anlegen.
            </p>
          </div>
        </mat-tab>
      </mat-tab-group>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="cancel()">Abbrechen</button>
      <button mat-raised-button color="primary" [disabled]="!canConfirm()" (click)="confirm()">
        Übernehmen
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .dialog-content { min-width: 360px; max-width: 90vw; }
      .tab-body { padding: 16px 4px; }
      .full { width: 100%; }
      .radio-list { display: flex; flex-direction: column; gap: 8px; max-height: 320px; overflow: auto; }
      .radio-item { padding: 4px 0; }
      .num {
        display: inline-block; min-width: 22px; padding: 0 6px; margin-right: 6px;
        background: #3b82f6; color: #fff; border-radius: 4px; font-size: 12px; text-align: center;
      }
      .empty { color: #6b7280; font-style: italic; }
    `
  ]
})
export class PlanDrawIndicatorDialogComponent implements OnInit {
  selectedExistingId: string | null = null;
  selectedIndikatorId: string | null = null;
  selectedRoomId: string | null = null;
  catalog: any[] = [];
  rooms: Raum[] = [];
  loading = true;

  constructor(
    public dialogRef: MatDialogRef<PlanDrawIndicatorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PlanDrawIndicatorDialogData,
    private objektStrukturService: ObjektStrukturService
  ) {}

  ngOnInit(): void {
    this.objektStrukturService.getIndikatoren().subscribe({
      next: (list) => {
        this.catalog = Array.isArray(list) ? list : ((list as any)?.data || []);
        if (this.catalog.length > 0) {
          this.selectedIndikatorId = this.catalog[0].id;
        }
      },
      error: () => (this.catalog = [])
    });

    if (this.data.sektionId) {
      this.objektStrukturService.getRaeume(this.data.sektionId).subscribe({
        next: (res) => {
          this.rooms = res?.data || [];
          if (this.rooms.length === 1) {
            this.selectedRoomId = this.rooms[0].id;
          }
          this.loading = false;
        },
        error: () => {
          this.rooms = [];
          this.loading = false;
        }
      });
    } else {
      this.loading = false;
    }
  }

  canConfirm(): boolean {
    return (
      !!this.selectedExistingId ||
      (!!this.selectedIndikatorId && !!this.selectedRoomId)
    );
  }

  confirm(): void {
    if (this.selectedExistingId) {
      const indicator = this.data.existing.find((r) => r.id === this.selectedExistingId);
      this.dialogRef.close({ mode: 'existing', indicator } as PlanDrawIndicatorDialogResult);
      return;
    }
    if (this.selectedIndikatorId && this.selectedRoomId) {
      this.dialogRef.close({
        mode: 'create',
        indikatorId: this.selectedIndikatorId,
        roomId: this.selectedRoomId
      } as PlanDrawIndicatorDialogResult);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
